import { Link } from "@tanstack/react-router";
import { ChevronLeftIcon, ChevronRightIcon, FileTextIcon, LibraryIcon } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { cn } from "~/lib/utils";
import type { Recommendation } from "../../../state/governance";
import { Button } from "../../ui/button";
import { HoverCard, HoverCardPopup, HoverCardTrigger } from "../../ui/hover-card";
import { suggestionTldr } from "../tldr";
import { KIND_LABELS, SUGGESTION_STATUS_PILL } from "./suggestionsView";
import {
  orderSuggestionQueue,
  suggestionQueuePage,
  suggestionDocumentLabel,
  type SuggestionDocumentLabel,
  type SuggestionQueueSort,
} from "./SuggestionQueue.logic";

const PRIORITY_TONE: Record<string, string> = {
  critical: "text-destructive",
  high: "text-destructive",
  medium: "text-warning",
  low: "text-muted-foreground",
};
const COLUMNS =
  "grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 md:grid-cols-[minmax(0,1fr)_12.5rem_5rem_6.5rem_1rem]";

/** A bounded review queue; every row opens the suggestion’s own decision page. */
export function SuggestionQueue({
  recommendations,
  orgSegment,
  sort,
  documentLabels,
}: {
  readonly recommendations: ReadonlyArray<Recommendation>;
  readonly orgSegment: string;
  readonly sort: SuggestionQueueSort;
  readonly documentLabels?: ReadonlyMap<string, SuggestionDocumentLabel> | undefined;
}) {
  const [requestedPage, setPage] = useState(0);
  const id = useId();
  const queueRef = useRef<HTMLDivElement>(null);
  const pageNavigation = useRef(false);
  const ordered = useMemo(
    () => orderSuggestionQueue(recommendations, sort),
    [recommendations, sort],
  );
  const page = suggestionQueuePage(ordered, requestedPage);
  useEffect(() => {
    if (requestedPage !== page.page) setPage(page.page);
    if (pageNavigation.current) {
      pageNavigation.current = false;
      queueRef.current
        ?.querySelector<HTMLAnchorElement>("a[data-suggestion-link]")
        ?.focus({ preventScroll: true });
      queueRef.current?.scrollIntoView({ block: "start" });
    }
  }, [requestedPage, page.page]);
  const move = (next: number) => {
    pageNavigation.current = true;
    setPage(next);
  };

  return (
    <div ref={queueRef} className="border-y border-border/60">
      <div
        aria-hidden
        className={cn(
          COLUMNS,
          "hidden items-center border-b border-border/60 bg-muted/30 px-4 py-2.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground md:grid",
        )}
      >
        <span>Suggested change</span>
        <span>Document</span>
        <span>Priority</span>
        <span>Status</span>
        <span />
      </div>
      <ul aria-label="Suggestion review queue" className="divide-y divide-border/60">
        {page.items.map((recommendation) => {
          const status = SUGGESTION_STATUS_PILL[recommendation.status];
          const document = suggestionDocumentLabel(recommendation, documentLabels);
          const documentName = document.displayName;
          const panelId = `${id}-${recommendation.id}`;
          return (
            <li key={recommendation.id} data-suggestion-id={recommendation.id}>
              <HoverCard>
                <HoverCardTrigger
                  render={
                    <Link
                      to="/$org/compliance/suggestions/$recommendationId"
                      params={{ org: orgSegment, recommendationId: recommendation.id }}
                      data-suggestion-link
                      aria-label={`Open suggestion: ${recommendation.title}`}
                      aria-describedby={`${panelId}-context`}
                      className={cn(
                        COLUMNS,
                        "w-full cursor-pointer items-center px-4 py-3 text-left transition-colors hover:bg-muted/40 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
                      )}
                    />
                  }
                >
                  <span id={`${panelId}-context`} className="sr-only">
                    {documentName}. File: {document.filename}. {KIND_LABELS[recommendation.kind]}.
                    Priority: {recommendation.severity ?? "unrated"}. Status: {status.label}.
                  </span>
                  <span className="min-w-0">
                    <span className="line-clamp-2 text-[13px] font-medium leading-5 text-foreground md:line-clamp-1">
                      {recommendation.title}
                    </span>
                    <span className="mt-0.5 block truncate text-xs leading-5 text-muted-foreground">
                      {suggestionTldr(recommendation)}
                    </span>
                    <span className="mt-1 flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground md:hidden">
                      {recommendation.policyId === null ? (
                        <LibraryIcon aria-hidden className="size-3 shrink-0" />
                      ) : (
                        <FileTextIcon aria-hidden className="size-3 shrink-0" />
                      )}
                      <span title={document.filename} className="truncate">
                        {documentName}
                      </span>
                    </span>
                  </span>
                  <span className="hidden min-w-0 md:block">
                    <span
                      title={document.filename}
                      className="line-clamp-2 text-xs leading-4 text-foreground"
                    >
                      {documentName}
                    </span>
                    <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                      {KIND_LABELS[recommendation.kind]}
                    </span>
                  </span>
                  <span
                    aria-label={`Severity: ${recommendation.severity ?? "unrated"}`}
                    className={cn(
                      "col-start-1 row-start-2 mt-2 text-[11px] font-medium capitalize md:col-auto md:row-auto md:mt-0",
                      PRIORITY_TONE[recommendation.severity ?? ""] ?? "text-muted-foreground",
                    )}
                  >
                    {recommendation.severity ?? "Unrated"}
                  </span>
                  <span className="col-start-1 row-start-2 mt-2 ml-16 w-fit text-[11px] text-muted-foreground md:col-auto md:row-auto md:mt-0 md:ml-0">
                    {status.label}
                  </span>
                  <ChevronRightIcon
                    aria-hidden
                    className="col-start-2 row-start-1 size-4 text-muted-foreground md:col-auto md:row-auto"
                  />
                </HoverCardTrigger>
                <HoverCardPopup
                  side="top"
                  align="start"
                  className="w-80 max-w-[calc(100vw-2rem)] p-4"
                >
                  <p className="text-xs font-semibold text-muted-foreground">TL;DR</p>
                  <p className="mt-2 text-sm leading-6">{suggestionTldr(recommendation, 320)}</p>
                  <p className="mt-3 text-xs text-muted-foreground">
                    {documentName} · {status.label}
                  </p>
                </HoverCardPopup>
              </HoverCard>
            </li>
          );
        })}
      </ul>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 px-4 py-3">
        <p role="status" aria-live="polite" className="text-xs tabular-nums text-muted-foreground">
          {page.from}–{page.to} of {page.total} suggestions
        </p>
        <nav aria-label="Suggestion pages" className="flex items-center gap-2">
          <Button
            size="icon"
            variant="ghost"
            aria-label="Previous page"
            disabled={page.page === 0}
            onClick={() => move(page.page - 1)}
            className="size-7"
          >
            <ChevronLeftIcon className="size-4" />
          </Button>
          <span className="text-xs tabular-nums text-muted-foreground">
            {page.page + 1} / {page.pageCount}
          </span>
          <Button
            size="icon"
            variant="ghost"
            aria-label="Next page"
            disabled={page.page + 1 === page.pageCount}
            onClick={() => move(page.page + 1)}
            className="size-7"
          >
            <ChevronRightIcon className="size-4" />
          </Button>
        </nav>
      </div>
    </div>
  );
}
